import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Code,
  Zap,
  Users,
  Briefcase,
  Search,
  ExternalLink,
  Clock,
  ArrowRight,
  FileText,
  Settings,
  Globe,
  Lightbulb,
} from 'lucide-react';
import { MARKETING_PRICING_URL } from '../constants/urls';
import { integrationRegistry } from '../integrations/registry';
import type { IntegrationCategory, IntegrationDefinition, IntegrationStatus } from '../integrations/types';

type DocLinkStatus = 'ready' | 'coming-soon' | 'external';

type DocItem = {
  title: string;
  description: string;
  status: DocLinkStatus;
  path?: string;
  /** When set, badge reflects registry status and the row stays navigable whenever `path` is set. */
  integrationStatus?: IntegrationStatus;
};

type DocSection = {
  id: string;
  title: string;
  icon: typeof Zap;
  description: string;
  estimatedTime: string;
  documents: DocItem[];
};

const CATEGORY_ORDER: IntegrationCategory[] = [
  'ai_assistants',
  'automation',
  'communication',
  'productivity',
  'development',
];

const CATEGORY_TITLE: Record<IntegrationCategory, string> = {
  ai_assistants: 'AI assistants',
  automation: 'Automation',
  communication: 'Communication',
  productivity: 'Productivity',
  development: 'Development',
  browser: 'Browser',
  mobile: 'Mobile',
  social_media: 'Social',
};

function buildRegistryIntegrationSections(): DocSection[] {
  const withLanding = Object.values(integrationRegistry).filter(
    (def): def is IntegrationDefinition & { components: { landingPagePath: string } } =>
      Boolean(def.components?.landingPagePath)
  );

  const byCategory = new Map<IntegrationCategory, IntegrationDefinition[]>();
  for (const cat of CATEGORY_ORDER) {
    byCategory.set(cat, []);
  }
  for (const def of withLanding) {
    const bucket = byCategory.get(def.category);
    if (bucket) {
      bucket.push(def);
    } else {
      byCategory.set(def.category, [def]);
    }
  }

  const sections: DocSection[] = [];
  for (const cat of CATEGORY_ORDER) {
    const list = byCategory.get(cat);
    if (!list?.length) continue;
    list.sort((a, b) => a.name.localeCompare(b.name));
    sections.push({
      id: `integrations-${cat}`,
      title: CATEGORY_TITLE[cat] ?? cat,
      icon: Globe,
      description: 'In-app integration landing pages from the product registry',
      estimatedTime: '—',
      documents: list.map((def) => ({
        title: def.name,
        description: def.description,
        path: def.components.landingPagePath!,
        status: 'ready' as const,
        integrationStatus: def.status,
      })),
    });
  }

  return sections;
}

const staticDocumentationSections: DocSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Zap,
    description: 'Start here — Chrome extension and future deep guides',
    estimatedTime: '15 min',
    documents: [
      {
        title: 'Chrome Extension',
        description: 'Browser-based memory capture for any webpage',
        path: '/docs/integrations/chrome-extension',
        status: 'ready',
      },
      {
        title: 'X Bookmarks',
        description: 'Import X bookmarks into searchable Memara memories',
        path: '/docs/integrations/x-bookmarks',
        status: 'ready',
      },
      {
        title: 'Long-form guides & API reference',
        description: 'Dedicated tutorial pages in this hub (beyond integration landings)',
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'guides',
    title: 'Use case guides',
    icon: Lightbulb,
    description: 'End-to-end examples (not yet available in this hub)',
    estimatedTime: '—',
    documents: [
      {
        title: 'Customer support bot',
        description: 'Build a memory-enabled support bot',
        status: 'coming-soon',
      },
      {
        title: 'Personal AI assistant',
        description: 'Create your personal memory assistant',
        status: 'coming-soon',
      },
      {
        title: 'Team knowledge base',
        description: 'Shared team memory spaces',
        status: 'coming-soon',
      },
      {
        title: 'Content creation AI',
        description: 'AI that remembers your style',
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'business',
    title: 'Business & enterprise',
    icon: Briefcase,
    description: 'Security, analytics, and deployment topics',
    estimatedTime: '—',
    documents: [
      {
        title: 'Security & compliance',
        description: 'Enterprise security features',
        status: 'coming-soon',
      },
      {
        title: 'Team management',
        description: 'User roles and permissions',
        status: 'coming-soon',
      },
      {
        title: 'Usage analytics',
        description: 'Track and optimize memory usage',
        status: 'coming-soon',
      },
      {
        title: 'Enterprise deployment',
        description: 'On-premise and custom deployments',
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'resources',
    title: 'Resources & support',
    icon: Users,
    description: 'Help, community, and pricing',
    estimatedTime: '10 min',
    documents: [
      {
        title: 'Support',
        description: 'Contact options and FAQs',
        path: '/support',
        status: 'ready',
      },
      {
        title: 'Blog',
        description: 'Product updates, launches, and deep dives',
        path: '/blog',
        status: 'ready',
      },
      {
        title: 'Plans & pricing',
        description: 'Tiers, limits, and billing (opens memara.io)',
        path: MARKETING_PRICING_URL,
        status: 'external',
      },
    ],
  },
];

type QuickLink = {
  title: string;
  description: string;
  path: string;
  icon: typeof Code;
  openInNewTab: boolean;
};

const quickLinks: QuickLink[] = [
  {
    title: 'Status page',
    description: 'Service status and uptime',
    path: 'https://status.memara.io',
    icon: Settings,
    openInNewTab: true,
  },
  {
    title: "What's new",
    description: 'Product updates and changelog-style notes',
    path: '/blog/memara-whats-new-2026',
    icon: FileText,
    openInNewTab: false,
  },
  {
    title: 'Roadmap',
    description: 'Upcoming features',
    path: '/roadmap',
    icon: ArrowRight,
    openInNewTab: false,
  },
  {
    title: 'Pricing',
    description: 'Plans and limits on memara.io',
    path: '/pricing',
    icon: Code,
    openInNewTab: false,
  },
];

function getRegistryStatusBadge(status: IntegrationStatus) {
  switch (status) {
    case 'available':
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          Ready
        </Badge>
      );
    case 'coming_soon':
      return (
        <Badge variant="outline" className="border-orange-200 text-orange-800">
          Coming soon
        </Badge>
      );
    case 'concept':
      return (
        <Badge variant="outline" className="border-violet-200 text-violet-800">
          Concept
        </Badge>
      );
    case 'in_progress':
    case 'in_development':
      return (
        <Badge variant="outline" className="border-blue-200 text-blue-800">
          In progress
        </Badge>
      );
    case 'release_1_4':
      return (
        <Badge variant="outline" className="border-amber-200 text-amber-900">
          Preview
        </Badge>
      );
    default:
      return null;
  }
}

export default function DocsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const documentationSections = useMemo(() => {
    const registrySections = buildRegistryIntegrationSections();
    const insertAt = 1;
    const out = [...staticDocumentationSections];
    out.splice(insertAt, 0, ...registrySections);
    return out;
  }, []);

  const filteredSections = documentationSections.filter(
    (section) =>
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.documents.some(
        (doc) =>
          doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const getStaticStatusBadge = (status: DocLinkStatus) => {
    switch (status) {
      case 'ready':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            Ready
          </Badge>
        );
      case 'coming-soon':
        return (
          <Badge variant="outline" className="border-orange-200 text-orange-800">
            Coming soon
          </Badge>
        );
      case 'external':
        return (
          <Badge variant="outline" className="border-blue-200 text-blue-800">
            External
          </Badge>
        );
      default:
        return null;
    }
  };

  const getDocBadge = (doc: DocItem) => {
    if (doc.integrationStatus) {
      return getRegistryStatusBadge(doc.integrationStatus);
    }
    return getStaticStatusBadge(doc.status);
  };

  const canOpenDoc = (doc: DocItem) => {
    if (!doc.path) return false;
    if (doc.integrationStatus) return true;
    return doc.status !== 'coming-soon';
  };

  const openDoc = (doc: DocItem) => {
    if (!canOpenDoc(doc) || !doc.path) {
      return;
    }
    if (doc.status === 'external' || doc.path.startsWith('http')) {
      window.open(doc.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(doc.path);
  };

  const openQuickLink = (link: QuickLink) => {
    if (link.openInNewTab || link.path.startsWith('http')) {
      window.open(link.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(link.path);
  };

  const chatgptPath =
    integrationRegistry.custom_gpt_action?.components?.landingPagePath ?? '/integrations/chatgpt';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Memara documentation</h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Integration guides and product links available in this app. Deeper reference material is
              marked coming soon or opens on memara.io where noted.
            </p>

            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="search"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <div className="space-y-8">
              {filteredSections.map((section) => {
                const Icon = section.icon;
                return (
                  <Card key={section.id} className="overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Icon className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <CardTitle className="text-xl">{section.title}</CardTitle>
                            <CardDescription className="text-base">{section.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{section.estimatedTime}</span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.documents.map((doc, index) => {
                          const canOpen = canOpenDoc(doc);
                          return (
                            <div
                              key={`${section.id}-${index}`}
                              className="flex items-start gap-3 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-medium text-gray-900">{doc.title}</h4>
                                  {getDocBadge(doc)}
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{doc.description}</p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={!canOpen}
                                  onClick={() => openDoc(doc)}
                                  className="w-full"
                                >
                                  {doc.status === 'external'
                                    ? 'Open external'
                                    : !canOpen
                                      ? 'Not available yet'
                                      : 'Open'}
                                  {doc.status === 'external' && (
                                    <ExternalLink className="w-4 h-4 ml-2" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {quickLinks.map((link, index) => {
                    const Icon = link.icon;
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => openQuickLink(link)}
                        className="flex w-full items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left border border-transparent hover:border-gray-200"
                      >
                        <Icon className="w-5 h-5 text-gray-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{link.title}</div>
                          <div className="text-sm text-gray-600">{link.description}</div>
                        </div>
                        {(link.openInNewTab || link.path.startsWith('http')) && (
                          <ExternalLink className="w-4 h-4 text-gray-400 shrink-0" aria-hidden />
                        )}
                      </button>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Popular starting points</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <button
                    type="button"
                    onClick={() => navigate(chatgptPath)}
                    className="w-full text-left space-y-1 p-2 rounded-md hover:bg-gray-50"
                  >
                    <div className="text-sm font-medium text-gray-900">ChatGPT Custom Actions</div>
                    <div className="text-xs text-gray-600">Custom GPTs with memory</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/docs/integrations/chrome-extension')}
                    className="w-full text-left space-y-1 p-2 rounded-md hover:bg-gray-50"
                  >
                    <div className="text-sm font-medium text-gray-900">Chrome extension</div>
                    <div className="text-xs text-gray-600">Capture from the browser</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/integrations/bare-api')}
                    className="w-full text-left space-y-1 p-2 rounded-md hover:bg-gray-50"
                  >
                    <div className="text-sm font-medium text-gray-900">REST API</div>
                    <div className="text-xs text-gray-600">REST quick start in-app</div>
                  </button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Need help?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Can&apos;t find what you&apos;re looking for? Our team is here to help.
                  </p>
                  <Button className="w-full" onClick={() => navigate('/support')}>
                    Contact support
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
